import React, { useState, useMemo } from 'react';
import { 
    Card, 
    Input, 
    Button, 
    Typography,
    Checkbox, 
    Badge, 
    Tooltip,
    Divider,
    Empty
} from 'antd';
import { 
    RightOutlined, 
    LeftOutlined, 
    SearchOutlined, 
    DoubleRightOutlined, 
    DoubleLeftOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { NubAccessLevelDTO } from '../types';

const { Title, Text } = Typography;
const { Search } = Input;

interface AccessLevelSelectorProps {
    accessLevels: NubAccessLevelDTO[];
    selectedAccessLevels: string[];
    onAccessLevelChange: (accessLevels: string[]) => void;
    disabled?: boolean;
}

export const AccessLevelSelector: React.FC<AccessLevelSelectorProps> = ({
    accessLevels,
    selectedAccessLevels,
    onAccessLevelChange,
    disabled = false
}) => {
    const { dark } = useTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
    const [selectedAssigned, setSelectedAssigned] = useState<string[]>([]);

    // Get available access levels (not selected)
    const availableAccessLevels = useMemo(() => {
        /* @ts-ignore */

        return accessLevels.filter(al => !selectedAccessLevels.includes(al.code));
    }, [accessLevels, selectedAccessLevels]);

    // Get selected access levels as objects
    const assignedAccessLevels = useMemo(() => {
        /* @ts-ignore */
        return accessLevels.filter(al => selectedAccessLevels.includes(al.code));
    }, [accessLevels, selectedAccessLevels]);

    // Filter available access levels based on search
    const filteredAvailableAccessLevels = useMemo(() => {
        if (!searchTerm) return availableAccessLevels;
        
        const searchLower = searchTerm.toLowerCase();
        return availableAccessLevels.filter(al =>
            al.name?.toLowerCase().includes(searchLower) ||
            al.code?.toLowerCase().includes(searchLower) ||
            (al.description && al.description.toLowerCase().includes(searchLower))
        );
    }, [availableAccessLevels, searchTerm]);

    // Filter assigned access levels based on search
    const filteredAssignedAccessLevels = useMemo(() => {
        if (!searchTerm) return assignedAccessLevels;
        
        const searchLower = searchTerm.toLowerCase();
        return assignedAccessLevels.filter(al =>
            al.name?.toLowerCase().includes(searchLower) ||
            al.code?.toLowerCase().includes(searchLower) ||
            (al.description && al.description.toLowerCase().includes(searchLower))
        );
    }, [assignedAccessLevels, searchTerm]);

    // Move selected available access levels to assigned
    const handleMoveToAssigned = () => {
        const newSelected = [...selectedAccessLevels, ...selectedAvailable];
        onAccessLevelChange(newSelected);
        setSelectedAvailable([]);
    };

    // Move all available access levels to assigned
    const handleMoveAllToAssigned = () => {
        const allAvailableCodes = filteredAvailableAccessLevels.map(al => al.code);
        const newSelected = [...selectedAccessLevels, ...allAvailableCodes];
        /* @ts-ignore */
        onAccessLevelChange(newSelected);
        setSelectedAvailable([]);
    };

    // Move selected assigned access levels to available
    const handleMoveToAvailable = () => {
        const newSelected = selectedAccessLevels.filter(code => !selectedAssigned.includes(code));
        onAccessLevelChange(newSelected);
        setSelectedAssigned([]);
    };

    // Move all assigned access levels to available
    const handleMoveAllToAvailable = () => {
        onAccessLevelChange([]);
        setSelectedAssigned([]);
    };

    // Get access level color based on permissions count
    const getAccessLevelColor = (accessLevel: NubAccessLevelDTO) => {
        const permissionCount = accessLevel.permissions?.length || 0;
        if (permissionCount >= 20) return '#f5222d'; // High permissions - red
        if (permissionCount >= 10) return '#fa8c16'; // Medium permissions - orange
        if (permissionCount >= 5) return '#faad14';  // Some permissions - yellow
        return '#52c41a'; // Few permissions - green
    };

    // Render access level item
    const renderAccessLevelItem = (accessLevel: NubAccessLevelDTO, isSelected: boolean, onSelect: (code: string, checked: boolean) => void) => (
        <div
            key={accessLevel.code}
            className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                isSelected 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            /* @ts-ignore */
            onClick={() => !disabled && onSelect(accessLevel.code, !isSelected)}
        >
            <div className="flex items-start space-x-3">
                <Checkbox
                    checked={isSelected}
                    /* @ts-ignore */
                    onChange={(e) => !disabled && onSelect(accessLevel.code, e.target.checked)}
                    disabled={disabled}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                        <Text strong className="text-sm">
                            {accessLevel.name}
                        </Text>
                        <Badge 
                            color={getAccessLevelColor(accessLevel)}
                            text={`${accessLevel.permissions?.length || 0} permissions`}
                            style={{ fontSize: '10px' }}
                        />
                    </div>
                    <Text type="secondary" className="text-xs block">
                        {accessLevel.code}
                    </Text>
                    {accessLevel.description && (
                        <Text type="secondary" className="text-xs block mt-1">
                            {accessLevel.description}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            {/* Search Control */}
            <div className="flex space-x-4">
                <Search
                    placeholder="Search access levels..."
                    allowClear
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    prefix={<SearchOutlined />}
                    className="flex-1"
                />
            </div>

            {/* Access Level Selector Layout */}
            <div className="grid grid-cols-5 gap-4 h-[500px]">
                {/* Available Access Levels (Left Panel) */}
                <div className="col-span-2">
                    <Card
                        title={
                            <div className="flex justify-between items-center">
                                <span>Available Access Levels</span>
                                <Badge count={filteredAvailableAccessLevels.length} showZero />
                            </div>
                        }
                        size="small"
                        className={`h-full ${dark ? 'bg-gray-800' : ''}`}
                        bodyStyle={{ padding: '12px', height: 'calc(100% - 57px)', overflowY: 'auto', maxHeight: '420px' }}
                    >
                        {filteredAvailableAccessLevels.length === 0 ? (
                            <Empty 
                                description="No available access levels"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ) : (
                            <div className="space-y-2">
                                {filteredAvailableAccessLevels.map(accessLevel =>
                                    renderAccessLevelItem(
                                        accessLevel,
                                        /* @ts-ignore */
                                        selectedAvailable.includes(accessLevel.code),
                                        (code, checked) => {
                                            if (checked) {
                                                setSelectedAvailable([...selectedAvailable, code]);
                                            } else {
                                                setSelectedAvailable(selectedAvailable.filter(c => c !== code));
                                            }
                                        }
                                    )
                                )}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Move Buttons (Center Panel) */}
                <div className="col-span-1 flex flex-col justify-center items-center space-y-3">
                    <Tooltip title="Move selected to assigned">
                        <Button
                            type="primary"
                            icon={<RightOutlined />}
                            onClick={handleMoveToAssigned}
                            disabled={disabled || selectedAvailable.length === 0}
                            size="large"
                        />
                    </Tooltip>
                    
                    <Tooltip title="Move all available to assigned">
                        <Button
                            type="default"
                            icon={<DoubleRightOutlined />}
                            onClick={handleMoveAllToAssigned}
                            disabled={disabled || filteredAvailableAccessLevels.length === 0}
                        />
                    </Tooltip>

                    <Divider />

                    <Tooltip title="Move all assigned to available">
                        <Button
                            type="default"
                            icon={<DoubleLeftOutlined />}
                            onClick={handleMoveAllToAvailable}
                            disabled={disabled || assignedAccessLevels.length === 0}
                        />
                    </Tooltip>

                    <Tooltip title="Move selected to available">
                        <Button
                            type="primary"
                            icon={<LeftOutlined />}
                            onClick={handleMoveToAvailable}
                            disabled={disabled || selectedAssigned.length === 0}
                            size="large"
                        />
                    </Tooltip>
                </div>

                {/* Assigned Access Levels (Right Panel) */}
                <div className="col-span-2">
                    <Card
                        title={
                            <div className="flex justify-between items-center">
                                <span>Assigned Access Levels</span>
                                <Badge count={assignedAccessLevels.length} showZero />
                            </div>
                        }
                        size="small"
                        className={`h-full ${dark ? 'bg-gray-800' : ''}`}
                        bodyStyle={{ padding: '12px', height: 'calc(100% - 57px)', overflowY: 'auto', maxHeight: '420px' }}
                    >
                        {filteredAssignedAccessLevels.length === 0 ? (
                            <Empty 
                                description="No assigned access levels"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ) : (
                            <div className="space-y-2">
                                {filteredAssignedAccessLevels.map(accessLevel =>
                                    renderAccessLevelItem(
                                        accessLevel,
                                        /* @ts-ignore */
                                        selectedAssigned.includes(accessLevel.code),
                                        (code, checked) => {
                                            if (checked) {
                                                setSelectedAssigned([...selectedAssigned, code]);
                                            } else {
                                                setSelectedAssigned(selectedAssigned.filter(c => c !== code));
                                            }
                                        }
                                    )
                                )}
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Summary */}
            <div className="flex justify-between items-center text-sm text-gray-500">
                <span>
                    {selectedAvailable.length > 0 && `${selectedAvailable.length} available selected`}
                </span>
                <span>
                    Total: {assignedAccessLevels.length} access levels assigned
                </span>
                <span>
                    {selectedAssigned.length > 0 && `${selectedAssigned.length} assigned selected`}
                </span>
            </div>
        </div>
    );
};
